/**
 * Gift message toggle (Version 1) — opens edit modal and syncs subtotal.
 */

if (!customElements.get('tw-gift-message-toggle')) {
  class GiftMessageToggle extends HTMLElement {
    constructor() {
      super();

      this.switchButton = this.querySelector('[data-gift-message-switch]');
      this.inner = this.querySelector('.tw-gift-message-toggle__inner');
      this.variantId = this.dataset.variantId;
      this.variantPrice = parseInt(this.dataset.variantPrice, 10) || 0;
      this.isEnabled = false;
      this.savedMessage = '';

      this.boundOnSwitchClick = this.onSwitchClick.bind(this);
      this.boundOnInnerClick = this.onInnerClick.bind(this);
    }

    connectedCallback() {
      if (!this.switchButton) {
        return;
      }

      this.switchButton.addEventListener('click', this.boundOnSwitchClick);
      this.inner?.addEventListener('click', this.boundOnInnerClick);
    }

    disconnectedCallback() {
      this.switchButton?.removeEventListener('click', this.boundOnSwitchClick);
      this.inner?.removeEventListener('click', this.boundOnInnerClick);
    }

    /**
     * @returns {GiftMessageModal|null}
     */
    getModal() {
      return document.getElementById('tw-gift-message-modal');
    }

    /**
     * @param {boolean} enabled
     * @param {{ skipModal?: boolean }} [options]
     */
    setEnabled(enabled, options = {}) {
      this.isEnabled = enabled;
      this.classList.toggle('is-active', enabled);

      if (this.classList.contains('tw-gift-message-toggle--extras')) {
        this.switchButton?.classList.toggle('added', enabled);
      }

      this.switchButton?.setAttribute('aria-pressed', enabled ? 'true' : 'false');

      const stepBlock = this.closest('step-block');
      if (stepBlock) {
        stepBlock.giftMessageEnabled = enabled;
        stepBlock.giftMessageVariantId = enabled ? this.variantId : null;
        stepBlock.giftMessagePrice = enabled ? this.variantPrice : 0;
        stepBlock.giftMessageText = enabled ? this.savedMessage : '';

        if (typeof stepBlock.updateSubTotal === 'function') {
          stepBlock.updateSubTotal();
        }
      }

      if (!enabled) {
        this.savedMessage = '';
      }

      this.dispatchEvent(
        new CustomEvent('giftmessagechange', {
          bubbles: true,
          detail: {
            enabled,
            variantId: this.variantId,
            price: this.variantPrice,
            message: this.savedMessage,
          },
        }),
      );

      if (enabled && !options.skipModal) {
        this.getModal()?.open(this, this.savedMessage);
      }

      if (!options.skipSync) {
        this.syncSiblingToggles(enabled, options);
      }
    }

    /**
     * @param {boolean} enabled
     * @param {{ skipModal?: boolean, skipSync?: boolean }} [options]
     */
    syncSiblingToggles(enabled, options = {}) {
      document.querySelectorAll('tw-gift-message-toggle').forEach((toggle) => {
        if (toggle === this) {
          return;
        }

        toggle.savedMessage = this.savedMessage;
        toggle.setEnabled(enabled, {
          ...options,
          skipModal: true,
          skipSync: true,
        });
      });
    }

    /**
     * Clears a saved message and turns the add-on off.
     */
    clearSavedMessage() {
      this.savedMessage = '';
      this.setEnabled(false, { skipModal: true });
    }

    /**
     * @param {string} message
     */
    applySavedMessage(message) {
      const trimmed = message.trim();

      if (trimmed === '') {
        this.clearSavedMessage();
        return;
      }

      this.savedMessage = trimmed;
      this.setEnabled(true, { skipModal: true });

      const stepBlock = this.closest('step-block');
      if (stepBlock) {
        stepBlock.giftMessageText = trimmed;
      }
    }

    onSwitchClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.isEnabled) {
        this.setEnabled(false);
        return;
      }

      this.setEnabled(true);
    }

    onInnerClick(event) {
      if (
        !this.isEnabled
        || event.target.closest('[data-gift-message-switch]')
        || event.target.closest('.extra-add')
      ) {
        return;
      }

      event.preventDefault();
      this.getModal()?.open(this, this.savedMessage);
    }
  }

  customElements.define('tw-gift-message-toggle', GiftMessageToggle);
}
